import React from "react";

import { ContentCard } from "../../../content-card";
import { ContentCardCaption } from "../../../content-card/caption";
import { Image } from "../../../image";
import { Pressable } from "../../../pressable";
import { useElementAppearsOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSnowplowContext } from "../../../../analytics/snowplow";
import { OPEN_LINK } from "../../../../constants/types/commands";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";

import styles from "./styles.modules.scss";

export type RegularArticleType = {
    type: "externalNewsEntry" | "newsEntry" | "esportNewsEntry";
    author?: string;
    ctaHidden?: boolean;
    categories: { color: string; title: string }[];
    external?: boolean;
    id: string;
    index: number;
    lang: string;
    listingSize: string;
    newUntil: number;
    postDate: number;
    section: "newsEntries" | "esportNewsEntries" | "communityEntries";
    thumbnail: ImageSourceSets;
    thumbnailAlt: ImageSourceSets;
    title: string;
    url?: string;
    like?: {
        counter: number;
        liked: boolean;
    };
};

export const Regular = ({
    author,
    categories,
    external,
    id,
    lang,
    type,
    listingSize,
    newUntil,
    postDate,
    thumbnail,
    ctaHidden,
    thumbnailAlt,
    title,
    url,
    index,
}: RegularArticleType) => {
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const [ref, isVisible] = useElementAppearsOnScreen<HTMLDivElement>();
    const { trackImpression } = useSnowplowContext();
    React.useEffect(() => {
        if (isVisible) {
            trackImpression(id, title);
        }
    }, [id, isVisible]);

    const hasOnClick = !!(url || !external);

    const onClick = () => {
        if (!hasOnClick) {
            return;
        }

        if (url?.startsWith("https://www.youtube.com/embed/")) {
            return;
        }

        external
            ? sendCommand(OPEN_LINK, {
                  url,
                  id,
                  label: title,
                  additionalInformation: { inboxFeedIndex: index },
              })
            : openNewsArticle(
                  id,
                  title,
                  type === "esportNewsEntry" ? "esport" : "news",
              );
    };

    return (
        <div
            data-testid={type}
            className={`article-regular ${
                styles.articleRegular
            } ${listingSize} ${styles[listingSize] || ""} `}
            ref={ref}
            onClick={onClick}
        >
            <Pressable
                disabled={!hasOnClick}
                animationSize={listingSize === "small" ? "small" : "large"}
            >
                <div className={`card ${styles.card}`}>
                    <ContentCard id={id} lang={lang} newUntil={newUntil}>
                        <div className={`content ${styles.content}`}>
                            <div className={`thumbnail ${styles.thumbnail}`}>
                                <Image
                                    sources={
                                        listingSize === "small"
                                            ? thumbnailAlt
                                            : thumbnail
                                    }
                                    cover={listingSize === "small"}
                                />
                            </div>

                            <div className={`caption ${styles.caption}`}>
                                <ContentCardCaption
                                    ctaHidden={ctaHidden}
                                    author={author}
                                    cardId={id}
                                    categories={categories}
                                    external={external}
                                    postDate={postDate}
                                    title={title}
                                />
                            </div>
                        </div>
                    </ContentCard>
                </div>
            </Pressable>
        </div>
    );
};
